(function () {
  'use strict';

  const API_PATH = '/apps/fh-bobbel/member-lab/feedback';
  const FEATURES = new Set(['lab', 'bobbel-designer']);
  const VISIBILITIES = new Set(['members', 'private']);
  const postsByBoard = new WeakMap();

  function text(root, key, fallback) {
    return (root.dataset && root.dataset[key]) || fallback;
  }

  function requestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') window.crypto.getRandomValues(bytes);
    else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    return Array.prototype.map.call(bytes, function (byte, index) {
      const hex = byte.toString(16).padStart(2, '0');
      return [4, 6, 8, 10].includes(index) ? `-${hex}` : hex;
    }).join('');
  }

  function bindingError(reason) {
    const error = new Error(reason || 'app_proxy_request_binding_unavailable');
    error.fhReason = reason || 'app_proxy_request_binding_unavailable';
    return error;
  }

  function sha256HexExactBody(bodyText) {
    if (!window.crypto || !window.crypto.subtle || typeof window.crypto.subtle.digest !== 'function' || typeof TextEncoder !== 'function') {
      return Promise.reject(bindingError());
    }
    return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(bodyText)).then(function (digest) {
      return Array.prototype.map.call(new Uint8Array(digest), function (byte) {
        return byte.toString(16).padStart(2, '0');
      }).join('');
    });
  }

  function postBoundJson(proxyBase, payload) {
    const bodyText = JSON.stringify(payload);
    const parsed = new URL(`${proxyBase || '/apps/fh-bobbel'}/member-lab/feedback`, window.location.origin);
    if (parsed.origin !== window.location.origin || parsed.pathname !== API_PATH || typeof window.fetch !== 'function') {
      return Promise.reject(bindingError('app_proxy_request_binding_invalid'));
    }
    parsed.searchParams.set('fh_method', 'POST');
    parsed.searchParams.set('fh_path', parsed.pathname);
    return sha256HexExactBody(bodyText).then(function (bodySha256) {
      parsed.searchParams.set('fh_body_sha256', bodySha256);
      return window.fetch(parsed.pathname + parsed.search, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: bodyText,
      });
    });
  }

  function validPost(value) {
    return value && typeof value === 'object'
      && typeof value.id === 'string'
      && typeof value.feature === 'string' && FEATURES.has(value.feature)
      && typeof value.visibility === 'string' && VISIBILITIES.has(value.visibility)
      && typeof value.nickname === 'string'
      && typeof value.body === 'string'
      && (value.parentId === null || typeof value.parentId === 'string' || typeof value.parentId === 'undefined')
      && typeof value.createdAt === 'string';
  }

  function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
  }

  function makeButton(label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  function payloadFingerprint(payload) {
    const stable = {};
    Object.keys(payload).filter(function (key) { return key !== 'requestId'; }).sort().forEach(function (key) { stable[key] = payload[key]; });
    return JSON.stringify(stable);
  }

  function submitLabelFor(root, isPrivate) {
    return isPrivate ? text(root, 'boardSendPrivate', 'Send privately') : text(root, 'boardPost', 'Publish post');
  }

  function submissionFor(form, payload) {
    const fingerprint = payloadFingerprint(payload);
    const existing = form._memberLabPending;
    if (existing) return { changed: existing.fingerprint !== fingerprint, pending: existing };
    const pending = { fingerprint, payload: Object.assign({}, payload) };
    form._memberLabPending = pending;
    return { changed: false, pending };
  }

  function clearSubmission(form) {
    delete form._memberLabPending;
    delete form.dataset.requestId;
    if (form._memberLabRecovery) form._memberLabRecovery.remove();
    delete form._memberLabRecovery;
  }

  function showRecovery(root, form, status, error) {
    if (!status) return;
    status.textContent = error && error.status === 409
      ? text(root, 'boardConflict', 'We could not confirm whether the last submission was saved. Check the discussion before starting a new submission.')
      : text(root, 'boardError', 'Your draft was kept. Please try again.');
    if (!form._memberLabPending) return;
    if (form._memberLabRecovery) form._memberLabRecovery.remove();
    const controls = document.createElement('div');
    controls.className = 'fh-member-lab__post-actions';
    controls.appendChild(makeButton(text(root, 'boardCheck', 'Check discussion'), function () { loadPosts(root, true); }));
    controls.appendChild(makeButton(text(root, 'boardNewSubmission', 'Start a new submission'), function () {
      clearSubmission(form);
      status.textContent = '';
    }));
    form._memberLabRecovery = controls;
    status.parentNode ? status.parentNode.appendChild(controls) : form.appendChild(controls);
  }

  function appendEditor(root, post, action, parent) {
    const form = document.createElement('form');
    form.className = 'fh-member-lab__inline-form';
    const nickname = document.createElement('input');
    nickname.name = 'nickname';
    nickname.maxLength = 40;
    nickname.required = true;
    const composer = root.querySelector('[data-member-lab-compose]');
    nickname.value = action === 'edit' ? post.nickname : (composer && composer.elements.nickname ? composer.elements.nickname.value : '');
    const body = document.createElement('textarea');
    body.name = 'body';
    body.maxLength = 1000;
    body.required = true;
    body.rows = 4;
    body.value = action === 'edit' ? post.body : '';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    const controls = document.createElement('div');
    controls.className = 'fh-member-lab__post-actions';
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = text(root, 'boardSave', 'Save');
    controls.appendChild(submit);
    controls.appendChild(makeButton(text(root, 'boardCancel', 'Cancel'), function () { form.remove(); }));
    if (action === 'reply') {
      const replyTo = document.createElement('strong');
      replyTo.textContent = `${text(root, 'boardReplyTo', 'Reply to')} ${post.nickname}`;
      form.appendChild(replyTo);
    }
    const nicknameLabel = document.createElement('label');
    nicknameLabel.textContent = text(root, 'boardNickname', 'Nickname');
    nicknameLabel.appendChild(nickname);
    const bodyLabel = document.createElement('label');
    bodyLabel.textContent = text(root, 'boardMessage', 'Your message');
    bodyLabel.appendChild(body);
    form.append(nicknameLabel, bodyLabel, status, controls);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (form.dataset.sending === 'true') return;
      const payload = {
        action,
        nickname: nickname.value.trim(),
        body: body.value.trim(),
      };
      if (action === 'edit') payload.id = post.id;
      if (action === 'reply') {
        payload.parentId = post.id;
        payload.requestId = form.dataset.requestId || requestId();
        form.dataset.requestId = payload.requestId;
      }
      if (!payload.nickname || !payload.body) return;
      const submission = action === 'reply' ? submissionFor(form, payload) : { changed: false, pending: { payload } };
      if (submission.changed) {
        showRecovery(root, form, status, { status: 409 });
        return;
      }
      form.dataset.sending = 'true';
      submit.disabled = true;
      sendWrite(root, submission.pending.payload).then(function () {
        clearSubmission(form);
        form.remove();
        loadPosts(root, true);
      }).catch(function (error) {
        showRecovery(root, form, status, error);
      }).finally(function () { form.dataset.sending = 'false'; submit.disabled = false; });
    });
    parent.appendChild(form);
  }

  function renderPost(root, post) {
    const article = document.createElement('article');
    article.className = 'fh-member-lab__post';
    if (post.parentId) article.classList.add('fh-member-lab__post--reply');
    const meta = document.createElement('div');
    meta.className = 'fh-member-lab__post-meta';
    const nickname = document.createElement('strong');
    nickname.textContent = post.nickname;
    const created = document.createElement('span');
    created.textContent = formatDate(post.createdAt);
    const feature = document.createElement('span');
    feature.textContent = post.feature === 'bobbel-designer'
      ? text(root, 'boardFeatureDesigner', 'Bobbel Designer')
      : text(root, 'boardFeatureLab', 'Member Lab');
    meta.append(nickname, created, feature);
    if (post.visibility === 'private') {
      const privateNote = document.createElement('span');
      privateNote.className = 'fh-member-lab__post-private';
      privateNote.textContent = text(root, 'boardPrivate', 'Private feedback');
      meta.appendChild(privateNote);
    }
    const body = document.createElement('p');
    body.className = 'fh-member-lab__post-body';
    body.textContent = post.body;
    const actions = document.createElement('div');
    actions.className = 'fh-member-lab__post-actions';
    if (!post.parentId) actions.appendChild(makeButton(text(root, 'boardReply', 'Reply'), function () { appendEditor(root, post, 'reply', article); }));
    if (post.canEdit === true) {
      actions.appendChild(makeButton(text(root, 'boardEdit', 'Edit'), function () { appendEditor(root, post, 'edit', article); }));
      const deleteButton = makeButton(text(root, 'boardDelete', 'Delete'), function () {
        if (deleteButton.disabled) return;
        if (typeof window.confirm === 'function' && !window.confirm(text(root, 'boardDeleteConfirm', 'Delete this post?'))) return;
        deleteButton.disabled = true;
        sendWrite(root, { action: 'delete', id: post.id }).then(function () {
          loadPosts(root, true);
        }).catch(function () {
          showStatus(root, text(root, 'boardError', 'Could not save your draft. Please try again.'));
        }).finally(function () { deleteButton.disabled = false; });
      });
      actions.appendChild(deleteButton);
    }
    article.append(meta, body, actions);
    return article;
  }

  function showStatus(root, message) {
    const status = root.querySelector('[data-member-lab-status]');
    if (status) status.textContent = message || '';
  }

  function appendThread(root, shell, post, childrenByParent) {
    const article = renderPost(root, post);
    shell.appendChild(article);
    const replies = childrenByParent.get(post.id) || [];
    if (replies.length) {
      const replyShell = document.createElement('div');
      replyShell.className = 'fh-member-lab__replies';
      replies.forEach(function (reply) { appendThread(root, replyShell, reply, childrenByParent); });
      article.appendChild(replyShell);
    }
  }

  function renderThreads(root, shell, posts) {
    const knownIds = new Set(posts.map(function (post) { return post.id; }));
    const childrenByParent = new Map();
    posts.forEach(function (post) {
      if (!post.parentId || !knownIds.has(post.parentId)) return;
      const replies = childrenByParent.get(post.parentId) || [];
      replies.push(post);
      childrenByParent.set(post.parentId, replies);
    });
    posts.forEach(function (post) {
      if (!post.parentId || !knownIds.has(post.parentId)) appendThread(root, shell, post, childrenByParent);
    });
  }

  function sendWrite(root, payload) {
    if (payload.action === 'create' && (!FEATURES.has(payload.feature) || !payload.requestId)) return Promise.reject(new Error('invalid_feedback_payload'));
    if (payload.action === 'reply' && (!payload.parentId || !payload.requestId)) return Promise.reject(new Error('invalid_feedback_payload'));
    if ((payload.action === 'edit' || payload.action === 'delete') && !payload.id) return Promise.reject(new Error('invalid_feedback_payload'));
    if (payload.action === 'create') payload.visibility = payload.visibility === 'private' ? 'private' : 'members';
    return postBoundJson(root.closest('[data-member-lab-gate]').dataset.proxyBase, payload).then(function (response) {
      if (!response.ok) {
        const error = new Error('feedback_write_failed');
        error.status = response.status;
        throw error;
      }
      return response.json();
    }).then(function (result) {
      if (!result || result.ok !== true) throw new Error('invalid_feedback_response');
      return result;
    });
  }

  function loadPosts(root, reset) {
    const board = root.querySelector('[data-member-lab-board]');
    const postsShell = root.querySelector('[data-member-lab-posts]');
    const more = root.querySelector('[data-member-lab-more]');
    if (!board || !postsShell || !more) return Promise.resolve();
    const cursor = reset ? '' : (board.dataset.cursor || '');
    const endpoint = new URL(API_PATH, window.location.origin);
    endpoint.searchParams.set('feature', 'all');
    if (cursor) endpoint.searchParams.set('cursor', cursor);
    more.disabled = true;
    if (reset) postsShell.textContent = text(root, 'boardLoading', 'Loading discussion…');
    return window.fetch(endpoint.pathname + endpoint.search, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) { if (!response.ok) throw new Error('feedback_read_failed'); return response.json(); })
      .then(function (result) {
        if (!result || !Array.isArray(result.posts) || !result.posts.every(validPost) || !(result.nextCursor === null || typeof result.nextCursor === 'string')) throw new Error('invalid_feedback_list');
        let posts = postsByBoard.get(board);
        if (reset || !posts) {
          posts = new Map();
          postsByBoard.set(board, posts);
        }
        result.posts.forEach(function (post) { posts.set(post.id, post); });
        postsShell.replaceChildren();
        renderThreads(root, postsShell, Array.from(posts.values()));
        if (!postsShell.children.length) {
          const empty = document.createElement('p');
          empty.className = 'fh-member-lab__empty';
          empty.textContent = text(root, 'boardEmpty', 'No posts yet.');
          postsShell.appendChild(empty);
        }
        board.dataset.cursor = result.nextCursor || '';
        more.hidden = !result.nextCursor;
      }).catch(function () {
        if (reset) postsShell.textContent = '';
        showStatus(root, text(root, 'boardError', 'Could not load the discussion. Please try again.'));
      }).finally(function () { more.disabled = false; });
  }

  function init(root) {
    const form = root.querySelector('[data-member-lab-compose]');
    const more = root.querySelector('[data-member-lab-more]');
    if (!form || !more || root.dataset.memberLabBoardReady === 'true') return;
    root.dataset.memberLabBoardReady = 'true';
    const privacy = form.elements.private;
    const submitButton = form.querySelector('[data-member-lab-submit]');
    const updateSubmitLabel = function () {
      if (submitButton) submitButton.textContent = submitLabelFor(root, Boolean(privacy && privacy.checked));
    };
    if (privacy) privacy.addEventListener('change', updateSubmitLabel);
    updateSubmitLabel();
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (form.dataset.sending === 'true') return;
      const payload = {
        action: 'create',
        feature: form.elements.feature.value,
        visibility: form.elements.private.checked ? 'private' : 'members',
        nickname: form.elements.nickname.value.trim(),
        body: form.elements.body.value.trim(),
        requestId: form.dataset.requestId || requestId(),
      };
      form.dataset.requestId = payload.requestId;
      if (!payload.nickname || !payload.body || !FEATURES.has(payload.feature)) return;
      const submission = submissionFor(form, payload);
      if (submission.changed) {
        showRecovery(root, form, root.querySelector('[data-member-lab-status]'), { status: 409 });
        return;
      }
      const submit = form.querySelector('[data-member-lab-submit]');
      form.dataset.sending = 'true';
      submit.disabled = true;
      sendWrite(root, submission.pending.payload).then(function () {
        form.reset();
        clearSubmission(form);
        showStatus(root, '');
        loadPosts(root, true);
      }).catch(function (error) { showRecovery(root, form, root.querySelector('[data-member-lab-status]'), error); })
        .finally(function () { form.dataset.sending = 'false'; submit.disabled = false; });
    });
    more.addEventListener('click', function () { loadPosts(root, false); });
    loadPosts(root, true);
  }

  window.MemberLabBoardRuntime = { postBoundJson, renderPost, renderThreads, validPost, submissionFor, submitLabelFor };
  document.addEventListener('member-lab-content-ready', function (event) { if (event.detail) init(event.detail); });
  document.querySelectorAll('[data-member-lab-content]:not([hidden])').forEach(init);
})();
